// Pure display policy for edge nodes. No React, no fetch — edge.test.ts covers it.
import type { Tone } from '../ui';
import type { EdgeCommand, EdgeCommandKind, EdgeLinks, EdgeLiveness, EdgeNode, EdgeTier, EdgeUplink } from './edgeTypes';

const LIVENESS_LABEL: Record<EdgeLiveness, string> = {
  online: 'Online',
  stale: 'Stale',
  offline: 'Offline',
  never: 'Never seen',
};

const LIVENESS_TONE: Record<EdgeLiveness, Tone> = {
  online: 'accent',
  stale: 'warning',
  offline: 'danger',
  never: 'neutral',
};

function knownLiveness(l: string | null | undefined): EdgeLiveness {
  return l === 'online' || l === 'stale' || l === 'offline' ? l : 'never';
}

export function livenessLabel(l: string | null | undefined): string {
  return LIVENESS_LABEL[knownLiveness(l)];
}

/** Badge tone for the liveness; status is always shown with its label too. */
export function livenessTone(l: string | null | undefined): Tone {
  return LIVENESS_TONE[knownLiveness(l)];
}

/** CSS modifier class, styled in edge.css from tokens. */
export function livenessClass(l: string | null | undefined): string {
  return `edge-live--${knownLiveness(l)}`;
}

const UPLINK_LABEL: Record<EdgeUplink, string> = {
  starlink: 'Starlink',
  lte: 'LTE',
  wifi: 'Wi-Fi',
  ethernet: 'Ethernet',
  unknown: 'Unknown',
};

export function uplinkLabel(u: string | null | undefined): string {
  return u && Object.prototype.hasOwnProperty.call(UPLINK_LABEL, u) ? UPLINK_LABEL[u as EdgeUplink] : 'Unknown';
}

const TIER_LABEL: Record<EdgeTier, string> = {
  P: 'Pi',
  'P+': 'Pi + Hailo',
  J: 'Jetson',
  unknown: 'Unknown',
};

export function tierLabel(t: string | null | undefined): string {
  return t && Object.prototype.hasOwnProperty.call(TIER_LABEL, t) ? TIER_LABEL[t as EdgeTier] : 'Unknown';
}

/** '12s ago', '5m ago', '3h ago', '2d ago'; 'never' when there is no timestamp. */
export function ago(iso: string | null | undefined, now: number): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 'never';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** 'm:ss' for a pairing-code countdown; never negative. */
export function countdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function enrolCommand(serverOrigin: string, code: string): string {
  return `sudo overwatch-edge enroll --server ${serverOrigin.replace(/\/+$/, '')} --code ${code}`;
}

export const TEMP_WARN_C = 80;
export const DISK_WARN_MB = 1024;

export function healthWarnings(node: Pick<EdgeNode, 'health' | 'quarantined'>): string[] {
  const out: string[] = [];
  const h = node.health || {};
  if (node.quarantined) out.push('Quarantined');
  if (h.clock_suspect) {
    const skew = typeof h.clock_skew_s === 'number' ? Math.round(Math.abs(h.clock_skew_s)) : null;
    out.push(skew !== null ? `Clock off by ${skew}s` : 'Clock unverified');
  }
  if (typeof h.temp_c === 'number' && h.temp_c >= TEMP_WARN_C) out.push(`Hot: ${Math.round(h.temp_c)}°C`);
  if (typeof h.disk_free_mb === 'number' && h.disk_free_mb < DISK_WARN_MB) {
    out.push(`Disk low: ${Math.round(h.disk_free_mb)} MB free`);
  }
  return out;
}

// ── LoRa back-channel (docs/EDGE_LORA.md) ──────────────────────────────

export const COMMAND_KINDS: EdgeCommandKind[] = ['ping', 'restart-uplink', 'restart-service', 'reboot'];

const COMMAND_LABEL: Record<EdgeCommandKind, string> = {
  ping: 'Ping',
  reboot: 'Reboot',
  'restart-uplink': 'Restart uplink',
  'restart-service': 'Restart service',
};

export function commandLabel(kind: string): string {
  return Object.prototype.hasOwnProperty.call(COMMAND_LABEL, kind) ? COMMAND_LABEL[kind as EdgeCommandKind] : kind;
}

/** Buttons only where a command can land: signing on, node paired, heard on LoRa at
 *  least once, and not the gateway itself. */
export function commandsVisible(
  node: Pick<EdgeNode, 'paired' | 'last_seen_lora' | 'lora_gateway'>,
  loraEnabled: boolean,
): boolean {
  return loraEnabled && node.paired && !!node.last_seen_lora && !node.lora_gateway;
}

export interface LinkChip {
  label: 'HTTPS' | 'LoRa';
  liveness: EdgeLiveness;
  tone: Tone;
}

export function linkChips(node: { links: EdgeLinks | undefined }): LinkChip[] {
  const l = node.links || { https: 'never', lora: 'never' };
  return [
    { label: 'HTTPS', liveness: knownLiveness(l.https), tone: livenessTone(l.https) },
    { label: 'LoRa', liveness: knownLiveness(l.lora), tone: livenessTone(l.lora) },
  ];
}

export function commandLine(cmd: Pick<EdgeCommand, 'kind' | 'arg'>): string {
  return cmd.arg ? `${commandLabel(cmd.kind)} ${cmd.arg}` : commandLabel(cmd.kind);
}

export function commandStatus(cmd: Pick<EdgeCommand, 'state' | 'result' | 'reason'>): { text: string; tone: Tone } {
  if (cmd.state === 'queued') return { text: 'Queued', tone: 'neutral' };
  if (cmd.state === 'sent') return { text: 'Sent, awaiting ack', tone: 'info' };
  if (cmd.state === 'expired') return { text: 'Expired, never acked', tone: 'warning' };
  const reason = cmd.reason ? `: ${cmd.reason}` : '';
  if (cmd.result === 'ok') return { text: `Done${reason}`, tone: 'accent' };
  if (cmd.result === 'refused') return { text: `Refused${reason}`, tone: 'danger' };
  return { text: `Failed${reason}`, tone: 'danger' };
}
